import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {GeneralStatistics, PlanningAcademicYears} from '../../../redux/Slice/generalSlice'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import CustomSelect from 'app/custom/CustomSelect'

interface EvaluationData {
  academic_year_id: number
  academic_year_name: string
  strongly_agree_count: number
  agree_count: number
  strongly_disagree_count: number
  somewhat_agree_count: number
}
const EvaluationDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [evaluationData, setEvaluationData] = useState<EvaluationData[]>([])
  const [annualData, setAnnualData] = useState<EvaluationData[]>([])
  const [academicYears, setAcademicYears] = useState<any>([])
  const [selectedAy, setSelectedAy] = useState<number[]>([])

  const fetchData = (ayIds: any[]) => {
    setLoader(true)
    const fd = new FormData()
    ayIds.forEach((id) => fd.append('academic_year_id[]', id))
    dispatch(GeneralStatistics(fd)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setEvaluationData(res.payload.courseEvForms || [])
        setAnnualData(res.payload.annualData || [])
      }
      setLoader(false)
    })
  }

  useEffect(() => {
    dispatch(PlanningAcademicYears()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const years = res.payload
        setAcademicYears(years)
        const sorted = [...years].sort((a: any, b: any) => a.name.localeCompare(b.name))
        const lastFour = sorted.slice(-2)
        const selectedIds = lastFour.map((y: any) => y.id)
        setSelectedAy(selectedIds)
        fetchData(selectedIds)
      }
    })
  }, [])

  const parseBackendData = (data: any[]): EvaluationData[] => {
    return data.map((item) => ({
      ...item,
      strongly_agree_count: Number(item.strongly_agree_count) || 0,
      agree_count: Number(item.agree_count) || 0,
      strongly_disagree_count: Number(item.strongly_disagree_count) || 0,
      somewhat_agree_count: Number(item.somewhat_agree_count) || 0,
    }))
  }

  const processEvaluationData = (data: EvaluationData[]) => {
    return data.map((year) => {
      const total =
        year.strongly_agree_count +
        year.agree_count +
        year.strongly_disagree_count +
        year.somewhat_agree_count

      if (total === 0) {
        return {
          ...year,
          strongly_agree_percent: 0,
          agree_percent: 0,
          strongly_disagree_percent: 0,
          somewhat_agree_percent: 0,
          total_responses: 0,
        }
      }

      const stronglyAgree = Math.round((year.strongly_agree_count / total) * 100)
      const agree = Math.round((year.agree_count / total) * 100)
      const stronglyDisagree = Math.round((year.strongly_disagree_count / total) * 100)

      // 🔥 Force last one to make total exactly 100
      const somewhatAgree = 100 - (stronglyAgree + agree + stronglyDisagree)

      return {
        ...year,
        strongly_agree_percent: stronglyAgree,
        agree_percent: agree,
        strongly_disagree_percent: stronglyDisagree,
        somewhat_agree_percent: somewhatAgree,
        total_responses: total,
      }
    })
  }

  const processedData = processEvaluationData(parseBackendData(evaluationData))

  const evaluationSeries = [
    {
      name: 'Excellent',
      data: processedData.map((y) => y.strongly_agree_percent),
    },
    {
      name: 'Très bon/ Very Good',
      data: processedData.map((y) => y.agree_percent),
    },
    {
      name: 'Pas Satisfaisant/ Not Satisfactory',
      data: processedData.map((y) => y.strongly_disagree_percent),
    },
    {
      name: 'Pas de tout satisfaisant/ Not at all Satisfactory',
      data: processedData.map((y) => y.somewhat_agree_percent),
    },
  ]

  // Chart options
  const evaluationOptions: ApexOptions = {
    chart: {
      type: 'bar',
      animations: {enabled: true, speed: 400},
    },
    colors: ['#1E88E5', '#43A047', '#FB8C00', '#E53935'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '40%',
        borderRadius: 6,
        dataLabels: {
          position: 'top',
          hideOverflowingLabels: false,
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -15,
      style: {
        colors: ['#000'],
        fontSize: '12px',
        fontWeight: 700,
      },
      formatter: (val) => {
        return Number(val) > 0 ? `${Math.round(Number(val))}%` : ''
      },
    },
    xaxis: {
      categories: processedData.map((y) => y.academic_year_name),
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val: number) => `${Math.round(Number(val))}%`,
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: (val: number, {seriesIndex, dataPointIndex}) => {
          const year = processedData[dataPointIndex]
          const counts = [
            year.strongly_agree_count,
            year.agree_count,
            year.strongly_disagree_count,
            year.somewhat_agree_count,
          ]
          return `${val.toFixed(1)}% (${counts[seriesIndex]} of ${year.total_responses})`
        },
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      markers: {
        radius: 12,
        width: 12,
        height: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 450,
          },
          plotOptions: {
            bar: {
              columnWidth: '45%',
            },
          },
          dataLabels: {
            style: {
              fontSize: '9px',
            },
          },
        },
      },
    ],
  }

  const processedAnnualData = processEvaluationData(parseBackendData(annualData))
  const annualSeries = [
    {
      name: 'Excellent',
      data: processedAnnualData.map((y) => y.strongly_agree_percent),
    },
    {
      name: 'Très bon/ Very Good',
      data: processedAnnualData.map((y) => y.agree_percent),
    },
    {
      name: 'Pas Satisfaisant/ Not Satisfactory',
      data: processedAnnualData.map((y) => y.strongly_disagree_percent),
    },
    {
      name: 'Pas de tout satisfaisant/ Not at all Satisfactory',
      data: processedAnnualData.map((y) => y.somewhat_agree_percent),
    },
  ]

  const annualOptions: ApexOptions = {
    ...evaluationOptions,
    xaxis: {
      categories: processedAnnualData.map((y) => y.academic_year_name),
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: (val: number, {seriesIndex, dataPointIndex}) => {
          const year = processedAnnualData[dataPointIndex]
          const counts = [
            year.strongly_agree_count,
            year.agree_count,
            year.strongly_disagree_count,
            year.somewhat_agree_count,
          ]
          return `${val}% (${counts[seriesIndex]} of ${year.total_responses})`
        },
      },
    },
  }

  if (loader) return <Loader />

  return (
    <div className='card'>
      <div className='card-body'>
        <div className='row'>
          <div className='col-md-10'>
            <label className='form-label fw-bold mb-1 required'>{t('LABEL.ACADEMIC_YEAR')}</label>

            <CustomSelect
              className='input'
              value={academicYears.filter((y) => selectedAy.includes(y.id))}
              onChange={(items: any) => setSelectedAy(items ? items.map((i: any) => i.id) : [])}
              options={academicYears}
              isMulti
            />
          </div>
          <div className='col-md-2 pt-7'>
            <button
              className='btn btn-outline-primary btn-sm fw-bold px-4 w-100'
              onClick={() => selectedAy.length > 0 && fetchData(selectedAy)}
            >
              <span className='fa fa-search mx-2'></span>
              {t('GLOBAL.SEARCH')}
            </button>
          </div>
        </div>
        <h5 className='text-center mb-3 mt-3'>{t('LABEL.COURSE_EVALUATION_SUMMARY')}</h5>
        {evaluationData.length > 0 ? (
          <>
            <ReactApexChart
              options={evaluationOptions}
              series={evaluationSeries}
              type='bar'
              height={350}
              width={'100%'}
            />
          </>
        ) : (
          <div className='text-center py-5'>{t('GLOBAL.DATA_NOT_AVAILABLE')}</div>
        )}

        <h5 className='text-center mb-3 mt-3'>{t('LABEL.ANNUAL_EVALUATION_SUMMARY')}</h5>
        {annualData.length > 0 ? (
          <>
            <ReactApexChart
              options={annualOptions}
              series={annualSeries}
              type='bar'
              height={350}
              width={'100%'}
            />
          </>
        ) : (
          <div className='text-center py-5'>{t('GLOBAL.DATA_NOT_AVAILABLE')}</div>
        )}
      </div>
    </div>
  )
}

export default EvaluationDashboard
